import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Copy, MessageCircle, Printer, Upload, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { rp, fmtDateTime, PAYMENT_KIND, waLink, waContacts } from "@/lib/format";
import { useSeo } from "@/lib/shop";
import { Loading, ErrorState } from "@/components/site/States";
import { OrderStatusBadge, PaymentBadge } from "@/components/site/Badges";

const inp = "w-full h-11 rounded-xl border-2 border-neutral-200 px-3 focus:border-neutral-900 outline-none bg-white";

export function Countdown({ to }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const ms = new Date(to).getTime() - now;
  if (ms <= 0) return <span>Batas waktu pembayaran telah lewat</span>;
  const h = Math.floor(ms / 3600e3), m = Math.floor((ms % 3600e3) / 60e3);
  return <span>Bayar dalam <b>{h} jam {m} menit</b> (sebelum {fmtDateTime(to)})</span>;
}

export function QrisPanel({ q, amount }) {
  return (
    <div className="rounded-2xl bg-white border-2 border-neutral-900 p-4 sm:p-6" data-testid="qris-panel">
      <div className="grid md:grid-cols-[minmax(0,420px)_1fr] gap-6 items-start">
        <div className="bg-white rounded-xl border p-2">
          <img src={q.image} alt={`QRIS ${q.merchant} NMID ${q.nmid}`} className="w-full h-auto object-contain [image-rendering:auto]" data-testid="qris-image" />
          <div className="flex gap-2 mt-2 print:hidden">
            <a href={q.image} target="_blank" rel="noreferrer" className="flex-1 text-center text-xs font-semibold underline" data-testid="qris-open-full">Buka ukuran penuh</a>
            <a href={q.image} download="QRIS-Bengkel-Toys-Hobbies.jpeg" className="flex-1 text-center text-xs font-semibold underline" data-testid="qris-download">Unduh QRIS</a>
          </div>
        </div>
        <div className="space-y-3 text-sm">
          <div><div className="text-xs font-bold uppercase text-neutral-500">Merchant</div><div className="font-heading font-extrabold text-lg" data-testid="qris-merchant">{q.merchant}</div><div className="font-mono text-xs">NMID: {q.nmid}</div></div>
          {amount != null && <div><div className="text-xs font-bold uppercase text-neutral-500">Nominal yang dibayar</div><div className="price text-2xl font-extrabold" data-testid="qris-amount">{rp(amount)}</div></div>}
          <ol className="list-decimal pl-5 space-y-1">
            <li>Buka aplikasi m-banking atau e-wallet yang mendukung QRIS (BCA, BRI, GoPay, OVO, DANA, ShopeePay, dll).</li>
            <li>Pilih menu <b>Scan / Bayar QRIS</b>, lalu scan kode di samping. Di HP, buka gambar ukuran penuh atau unduh lalu pilih dari galeri.</li>
            <li>Pastikan nama merchant <b>{q.merchant}</b>, lalu ketik nominal <b>persis</b> sesuai tagihan.</li>
            <li>Simpan screenshot bukti pembayaran, lalu unggah di form bukti pembayaran.</li>
          </ol>
          <p className="text-xs rounded-lg bg-amber-50 border border-amber-200 p-2 text-amber-900">QRIS ini statis: pembayaran <b>tidak terverifikasi otomatis</b>. Status menjadi Lunas setelah admin mengecek dan menyetujui bukti pembayaran.</p>
        </div>
      </div>
    </div>
  );
}

function Banks({ n }) {
  const copy = (v) => navigator.clipboard?.writeText(v.replace(/\s/g, "")).then(() => toast.success("Nomor rekening disalin"));
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="nota-banks">
      {n.bank_accounts.map((b, i) => (
        <div key={i} className="rounded-xl border-2 border-neutral-900 p-4 bg-white" data-testid={`nota-bank-${i}`}>
          <div className="text-xs font-bold text-[var(--bk-blue)]">BANK {b.bank}</div>
          <div className="font-mono text-xl font-bold tracking-wide">{b.number}</div>
          <div className="text-sm">a.n. {b.holder}</div>
          <button type="button" onClick={() => copy(b.number)} className="mt-2 text-xs font-semibold inline-flex items-center gap-1 underline print:hidden" data-testid={`nota-copy-bank-${i}`}><Copy className="h-3.5 w-3.5" />Salin nomor</button>
        </div>
      ))}
    </div>
  );
}

export function ProofForm({ n, no, t, onDone, qris = false }) {
  const [f, setF] = useState({ amount: n.amount_due || "", bank: qris ? "QRIS" : n.bank_accounts[0]?.bank || "", sender_name: n.customer.name, file: null });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.file) return toast.error("Pilih foto bukti transfer");
    const fd = new FormData();
    fd.append("file", f.file);
    fd.append("amount", String(f.amount));
    fd.append("bank", f.bank);
    fd.append("sender_name", f.sender_name);
    setBusy(true);
    try {
      onDone((await api.post(`/nota/${no}/proof`, fd, { params: { t } })).data);
      toast.success("Bukti pembayaran terkirim. Silakan konfirmasi ke WhatsApp.");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl bg-neutral-50 border p-4 sm:p-5 grid sm:grid-cols-2 gap-3 print:hidden" data-testid="proof-form">
      <h3 className="sm:col-span-2 font-heading font-bold text-lg">Unggah Bukti Pembayaran</h3>
      <label className="text-sm font-semibold">Nominal dibayar (Rp)<input data-testid="proof-amount" required type="number" min="1" className={inp} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></label>
      <label className="text-sm font-semibold">Metode / tujuan<select data-testid="proof-bank" className={inp} value={f.bank} onChange={(e) => setF({ ...f, bank: e.target.value })}>{n.qris?.enabled && <option value="QRIS">QRIS {n.qris.merchant}</option>}{n.bank_accounts.map((b) => <option key={b.number} value={b.bank}>Transfer {b.bank} {b.number}</option>)}</select></label>
      <label className="text-sm font-semibold">Nama pembayar (pemilik rekening / akun e-wallet)<input data-testid="proof-sender" required className={inp} value={f.sender_name} onChange={(e) => setF({ ...f, sender_name: e.target.value })} /></label>
      <label className="text-sm font-semibold">Foto / screenshot bukti<input data-testid="proof-file" required type="file" accept="image/*" className="block w-full text-sm mt-2" onChange={(e) => setF({ ...f, file: e.target.files[0] || null })} /></label>
      <button data-testid="proof-submit" disabled={busy} className="btn-ink sm:col-span-2"><Upload className="h-4 w-4" />{busy ? "Mengunggah..." : "Kirim Bukti Pembayaran"}</button>
    </form>
  );
}

function Items({ n }) {
  return (
    <table className="w-full text-sm" data-testid="nota-items">
      <thead className="text-left text-xs uppercase text-neutral-500 border-b"><tr><th className="py-2">Produk</th><th className="py-2 text-center">Qty</th><th className="py-2 text-right">Harga</th><th className="py-2 text-right">Subtotal</th></tr></thead>
      <tbody className="divide-y">
        {n.items.map((it, i) => (
          <tr key={i}>
            <td className="py-2"><div className="font-semibold">{it.name}{it.variant && ` (${it.variant})`}</div><div className="text-xs text-neutral-500 font-mono">{it.sku}</div>{n.kind === "po" && <div className="text-xs text-neutral-600">DP {rp(it.dp_total)} · sisa {rp(it.remaining_total)}{it.eta && ` · ETA ${it.eta}`}</div>}</td>
            <td className="py-2 text-center">{it.qty}</td>
            <td className="py-2 text-right price">{rp(it.unit_price)}{it.discount_per_unit > 0 && <div className="text-xs line-through text-neutral-400">{rp(it.normal_price)}</div>}</td>
            <td className="py-2 text-right price font-semibold">{rp(it.subtotal)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Totals({ n }) {
  const Row = ({ l, v, b, testid }) => <div className="flex justify-between gap-6" data-testid={testid}><span>{l}</span><span className={`price ${b ? "font-extrabold" : ""}`}>{v}</span></div>;
  return (
    <div className="ml-auto w-full sm:w-80 text-sm space-y-1">
      {n.discount_total > 0 && <Row l="Diskon" v={`-${rp(n.discount_total)}`} />}
      <Row l="Total produk" v={rp(n.total)} b testid="nota-total" />
      {n.kind === "po" && <><Row l="DP" v={rp(n.dp_total)} /><Row l="Sisa pelunasan" v={rp(n.remaining_total)} /></>}
      <Row l="Ongkir" v={n.shipping_fee === null ? (n.fulfillment === "pickup" ? "-" : "ditagih terpisah") : rp(n.shipping_fee)} />
      <Row l="Sudah dibayar (terverifikasi)" v={rp(n.paid_amount)} />
    </div>
  );
}

export default function Nota() {
  const { no } = useParams();
  const [sp] = useSearchParams();
  const t = sp.get("t") || "";
  const [n, setN] = useState(null);
  const [err, setErr] = useState("");
  useSeo(`Nota ${no}`, "Nota pesanan Bengkel Toys & Hobbies");
  useEffect(() => {
    api.get(`/nota/${no}`, { params: { t } }).then((r) => setN(r.data)).catch((e) => setErr(errMsg(e)));
  }, [no, t]);
  if (err) return <div className="container-x py-16"><ErrorState text={err} /></div>;
  if (!n) return <Loading />;
  const closed = n.status === "dibatalkan" || n.payment_status === "lunas";
  const pending = n.payments.some((p) => p.status === "pending");
  const lastProof = [...n.payments].reverse().find((p) => p.has_proof);
  const notaUrl = window.location.href;
  const waMsg = lastProof
    ? `Halo ${n.store.store_name}, saya sudah transfer untuk nota *${n.order_no}* sebesar ${rp(lastProof.amount)} (${lastProof.method}) atas nama ${n.customer.name}. Bukti transfer sudah saya unggah di nota: ${notaUrl}`
    : `Halo ${n.store.store_name}, saya ingin konfirmasi nota *${n.order_no}* atas nama ${n.customer.name}. Total ${rp(n.amount_due)}. Link nota: ${notaUrl}`;
  return (
    <div className="container-x py-8 sm:py-12 max-w-4xl print:py-0 print:max-w-none" data-testid="nota-page">
      <div className="flex flex-wrap gap-2 justify-end mb-4 print:hidden">
        <button onClick={() => window.print()} className="btn-brand" data-testid="nota-print"><Printer className="h-4 w-4" />Unduh / Cetak PDF</button>
      </div>
      <article className="bg-white rounded-2xl border-2 border-neutral-900 p-5 sm:p-8 space-y-6 print:border-0 print:p-0">
        <header className="flex flex-wrap justify-between gap-4 border-b pb-5">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-sm.webp" alt="Bengkel Toys & Hobbies" className="h-16 w-auto" />
            <div className="text-sm"><div className="font-heading font-extrabold text-lg">{n.store.store_name}</div><div className="text-neutral-600">{n.store.address}</div><div className="text-neutral-600">{waContacts(n.store).map((c) => `WA ${c.label} ${c.display}`).join(" · ")} · IG @{n.store.instagram}</div></div>
          </div>
          <div className="text-right">
            <div className="text-xs font-bold uppercase text-neutral-500">Nota {n.kind === "po" ? "Pre-Order" : "Ready Stock"}</div>
            <div className="font-mono text-2xl font-bold" data-testid="nota-order-no">{n.order_no}</div>
            <div className="text-xs text-neutral-500">{fmtDateTime(n.created_at)}</div>
            <div className="flex gap-1 justify-end mt-2"><span data-testid="nota-status"><OrderStatusBadge status={n.status} /></span><span data-testid="nota-payment-status"><PaymentBadge status={n.payment_status} /></span></div>
          </div>
        </header>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div><div className="text-xs font-bold uppercase text-neutral-500">Pelanggan</div><div className="font-semibold">{n.customer.name}</div><div>{n.customer.phone.replace(/^62/, "0").replace(/(\d{4})(\d{4})(\d+)/, "$1-$2-$3")}</div></div>
          <div><div className="text-xs font-bold uppercase text-neutral-500">Pengambilan</div><div>{n.fulfillment === "pickup" ? "Ambil di toko" : `Dikirim ke: ${n.customer.address}`}</div>{n.notes && <div className="italic mt-1">Catatan: {n.notes}</div>}</div>
        </div>
        <Items n={n} />
        <Totals n={n} />
        {!closed && (
          <section className="rounded-2xl bg-[var(--bk-yellow)] border-2 border-neutral-900 p-5 space-y-4" data-testid="nota-pay-box">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div><div className="text-sm font-bold">{n.kind === "po" && n.paid_amount < n.dp_total ? "Bayar DP sebesar" : "Jumlah yang perlu ditransfer"}</div><div className="price text-3xl font-extrabold" data-testid="nota-amount-due">{rp(n.amount_due)}</div></div>
              {n.pay_deadline && n.paid_amount === 0 && !pending && <div className="text-sm flex items-center gap-1" data-testid="nota-deadline"><Clock className="h-4 w-4" /><Countdown to={n.pay_deadline} /></div>}
            </div>
            {n.qris?.enabled && (
              <Link to={`/bayar/${n.order_no}?t=${t}`} className="btn-ink w-full print:hidden" data-testid="nota-pay-qris">{n.payment_method === "qris" ? "Bayar dengan QRIS" : "Atau bayar dengan QRIS"}</Link>
            )}
            <div className="text-xs font-bold uppercase">Transfer bank</div>
            <Banks n={n} />
            {n.fulfillment === "delivery" && <p className="text-xs">Ongkos kirim belum termasuk dan akan ditagih terpisah setelah dikonfirmasi admin.</p>}
          </section>
        )}
        {n.status === "dibatalkan" && <p className="rounded-lg bg-red-50 border border-red-200 p-3 text-red-800 text-sm font-semibold" data-testid="nota-cancelled">Pesanan ini telah dibatalkan.</p>}
        {n.payments.length > 0 && (
          <div className="text-sm" data-testid="nota-payments">
            <h3 className="font-bold mb-2">Riwayat pembayaran</h3>
            {n.payments.map((p, i) => <div key={i} className="flex justify-between border-b py-1.5"><span>{PAYMENT_KIND[p.kind]} · {p.method} · {fmtDateTime(p.created_at)}</span><span><b className="price">{rp(p.amount)}</b> <span className={`text-xs font-semibold ${p.status === "verified" ? "text-emerald-700" : p.status === "rejected" ? "text-red-600" : "text-amber-700"}`}>{p.status === "verified" ? "Terverifikasi" : p.status === "rejected" ? "Ditolak" : "Menunggu verifikasi"}</span></span></div>)}
          </div>
        )}
        <p className="text-xs text-neutral-500">Pembayaran dianggap sah setelah diverifikasi admin {n.store.store_name}. Simpan nota ini sebagai bukti pemesanan.</p>
      </article>
      {!closed && pending && <p className="mt-6 rounded-lg bg-sky-50 border border-sky-200 p-3 text-sky-900 text-sm print:hidden" data-testid="nota-proof-sent">Bukti transfer terkirim dan sedang menunggu verifikasi admin. Jangan lupa konfirmasi lewat WhatsApp.</p>}
      {!closed && !pending && <div className="mt-6"><ProofForm n={n} no={no} t={t} onDone={setN} qris={n.payment_method === "qris"} /></div>}
      <div className="mt-6 flex flex-col sm:flex-row gap-3 print:hidden">
        <a href={waLink(n.wa_target || n.store.whatsapp, waMsg)} target="_blank" rel="noreferrer" className="btn-wa sm:flex-1" data-testid="nota-confirm-wa"><MessageCircle className="h-4 w-4" />{closed ? "Hubungi Admin via WhatsApp" : lastProof ? "Konfirmasi Transfer ke WhatsApp" : "Hubungi Admin via WhatsApp"}</a>
        <Link to={`/lacak-pesanan?no=${n.order_no}`} className="btn-brand sm:flex-1" data-testid="nota-track-link">Lacak Pesanan</Link>
      </div>
    </div>
  );
}
